

export function miniImage(mini) {
    return mini.src || (mini.creature ? mini.creature.src : "");
}

// A creature's variants are its primary image plus any extras in `images`
// (MINI-23). Each variant carries its own heightRatio so a group can size
// every mini to the tallest one. Normalizes legacy string entries and always
// includes the primary as variant 0. Falls back to the creature's heightRatio
// when a variant's own ratio is unknown.
export function creatureVariants(creature) {
    if (!creature) return [];
    const base = { src: creature.src, heightRatio: creature.heightRatio };
    const extras = Array.isArray(creature.images) ? creature.images : [];
    const normalized = extras.map(v =>
        typeof v === 'string'
            ? { src: v, heightRatio: creature.heightRatio }
            : { src: v.src, heightRatio: v.heightRatio != null ? v.heightRatio : creature.heightRatio }
    );
    return [base, ...normalized].filter(v => v.src);
}

// Tallest heightRatio across a creature's variants — the group sizes every
// mini to this so mixed-height art lines up on a shared fold/base line.
export function maxHeightRatio(creature) {
    const variants = creatureVariants(creature);
    return variants.reduce(
        (m, v) => Math.max(m, v.heightRatio || 0),
        (creature && creature.heightRatio) || 0
    );
}

// Resolve a group's render style (MINI-24): an explicit per-group override wins,
// otherwise fall back to the creature's library default, otherwise 'standee'.
// Mirrors how `size` falls back from group to creature.
export function effectiveRenderStyle(group) {
    return (group && group.renderStyle)
        || (group && group.creature && group.creature.renderStyle)
        || 'standee';
}

// Loads an image to capture its aspect ratio (heightRatio = h / w) so a
// variant can be sized without re-measuring at render time.
export function measureImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = function () {
            resolve({
                src,
                srcWidth: this.width,
                srcHeight: this.height,
                heightRatio: this.height / this.width,
            });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = src;
    });
}

export function fileToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Extracts a usable image src from a drop/paste DataTransfer: a dropped image
// file, or a web <img> dragged from a page. Returns a data: URL for local
// files/blobs and the original URL for http(s)/data images. Resolves to null
// when nothing usable is found.
export function extractImageSrcFromDrop(dataTransfer) {
    if (!dataTransfer) return Promise.resolve(null);

    if (dataTransfer.files && dataTransfer.files.length > 0) {
        const file = dataTransfer.files[0];
        if (!file.type.startsWith('image/')) return Promise.resolve(null);
        return fileToDataUrl(file);
    }

    if (dataTransfer.types && dataTransfer.types.includes('text/html')) {
        const html = dataTransfer.getData('text/html');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const img = doc.getElementsByTagName('img')[0];
        const src = img && img.getAttribute('src');
        if (!src) return Promise.resolve(null);
        if (src.startsWith('blob:')) {
            return fetch(src).then(r => r.blob()).then(fileToDataUrl).catch(() => null);
        }
        if (/^(https?:\/\/|data:image\/)/i.test(src)) return Promise.resolve(src);
    }

    return Promise.resolve(null);
}
